/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo} from 'react'
import {withRouter} from 'react-router-dom'
import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isEditing} from '@builder.io/sdk-react'
import {Redirect} from 'react-router-dom'

// Components
import Skeleton from '@/components/skeleton'
import {configureRoutes} from '@salesforce/retail-react-app/app/utils/routes-utils'

// Default PWA kit routes
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import PropTypes from 'prop-types'
import {isClient} from '@/utils/common'
import {canVisitLesson} from 'hec/utils/guest-lesson-tracker'

const fallback = <Skeleton style={{height: '75vh', width: '100%'}} />

const EditModal = loadable(() => import('./pages/builder-previews/edit-modal'), {fallback})
const EditNavDrawer = loadable(() => import('./pages/builder-previews/edit-nav-drawer'), {fallback})
const EditPopoverContent = loadable(() => import('./pages/builder-previews/edit-popover-content'), {
    fallback
})
const EditModel = loadable(() => import('./pages/builder-previews/edit-model'), {fallback})

const EditLearningCenterPageTemplate = loadable(
    () => import('./pages/builder-previews/edit-learning-center-page-template'),
    {
        fallback
    }
)
const Page = loadable(() => import('./pages/page'), {fallback})
const LearningCenterArticlePage = loadable(
    () => import('./pages/learning-center/articles/LearningCenterArticlePage'),
    {fallback}
)
const LearningCenterPage = loadable(() => import('./pages/learning-center/LearningCenterPage'), {
    fallback
})
const PasswordlessComplete = loadable(() => import('./pages/passwordless/passwordlessComplete'), {
    fallback
})
const PageNotFound = loadable(() => import('@salesforce/retail-react-app/app/pages/page-not-found'))

function PrivateRoute({children, redirectPath}) {
    const {customerType} = useCustomerType()
    const locationPath = window.location.pathname
    const lessonRegex = /\/learning-center\/([^/]+)/
    const match = locationPath.match(lessonRegex)
    const currentLesson = match?.[1]
    const isGuestAllowed = canVisitLesson(currentLesson)

    if (isEditing()) {
        return children
    }
    switch (customerType) {
        case null:
            return <div className="h-96 flex justify-center items-center">Loading...</div>
        case 'guest':
            if (isGuestAllowed) {
                return children
            }
            if (redirectPath) {
                return <Redirect to={redirectPath} />
            }
            return <div className="h-96 flex justify-center items-center">Unauthorized</div>
        case 'registered':
            return children
        default:
            throw new Error(`Unknown customerType: ${customerType}`)
    }
}

const routes = [
    {
        path: '/edit/modal-content/:id',
        component: EditModal,
        exact: true
    },
    {
        path: '/edit/nav-drawer-content/:id',
        component: EditNavDrawer,
        exact: true
    },
    {
        path: '/edit/popover-content/:id',
        component: EditPopoverContent,
        exact: true
    },
    {
        path: '/edit/learning-center-templates/:id',
        component: EditLearningCenterPageTemplate,
        exact: true
    },
    {
        path: '/edit/:model/:id',
        component: EditModel,
        exact: true
    },
    {
        path: '/learning-center/articles/*',
        component: LearningCenterArticlePage
    },
    {
        path: '/learning-center/*',
        component: LearningCenterPage
    },
    {
        path: '/auth/passwordless-complete',
        component: PasswordlessComplete
    },

    {
        path: '/',
        component: Page
    },
    {
        path: '*',
        component: Page
    }
]

// Remove SFRA/SiteGenesis routes from PWA Kit
//const ecomRoutes = ['/cart', '/checkout', '/login', '*']
const ecomRoutes = ['/cart', '/checkout', '*']

const hybridRoutes = [
    ...routes.filter((route) => !ecomRoutes.includes(route.path)),
    {
        path: '*',
        component: withRouter((props) => {
            const config = getConfig()
            const {location} = props
            const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search])
            const {site} = useMultiSite()
            const siteId = site && site.id ? site.id : config?.app?.defaultSite

            if (isClient) {
                useEffect(() => {
                    const handleRedirect = () => {
                        const newURL = new URL(window.location.href)
                        if (!urlParams.has('redirected')) {
                            newURL.searchParams.append('redirected', '1')
                            newURL.pathname = `/s/${siteId}/${window.location.pathname
                                .split('/')
                                .slice(2)
                                .join('/')}`
                            window.location.replace(newURL)
                        }
                    }

                    // Call handleRedirect immediately on mount
                    handleRedirect()

                    // Add a popstate event listener to detect changes
                    window.addEventListener('popstate', handleRedirect)

                    return () => {
                        window.removeEventListener('popstate', handleRedirect)
                    }
                }, [siteId, urlParams])
            }

            if (urlParams.has('redirected')) {
                return <PageNotFound {...props} />
            }
            return null
        })
    }
]

PrivateRoute.propTypes = {
    children: PropTypes.node.isRequired,
    redirectPath: PropTypes.string
}
export default () => {
    const config = getConfig()
    const routesToConfigure = config.app.enableHybrid ? hybridRoutes : routes
    return configureRoutes(routesToConfigure, config, {
        ignoredRoutes: ['/callback', '*']
    })
}
